import {
  FORMULA_FUNCTION_CATEGORIES,
  FORMULA_FUNCTION_SIGNATURES,
  getFormulaFunctionCategory,
  type FormulaFunctionCategory,
  type FormulaFunctionSignature,
} from './functionCatalog'

export type InsertFunctionCategory = 'All' | FormulaFunctionCategory

export const INSERT_FUNCTION_CATEGORY_OPTIONS: InsertFunctionCategory[] = [...FORMULA_FUNCTION_CATEGORIES]

export function buildInsertFunctionArgumentSlots(signature: FormulaFunctionSignature): string[] {
  const fixed = signature.args.filter((arg) => arg !== '...')
  if (signature.args.includes('...')) {
    const base = fixed[0]?.replace(/\[|\]|\d+/g, '') || 'value'
    return [...fixed, `[${base}${fixed.length + 1}]`]
  }
  return fixed
}

export function formatInsertFunctionSignature(signature: FormulaFunctionSignature): string {
  return `${signature.name}(${signature.args.join(', ')})`
}

export function buildInsertFunctionFormulaPreview(name: string, args: string[]): string {
  const usedArgs = args.map((arg) => String(arg ?? '').trim()).filter(Boolean)
  return `=${String(name || '').trim().toUpperCase()}(${usedArgs.join(', ')})`
}

export function buildInsertFunctionFilteredSignatures(args: {
  searchText?: string | null
  category?: InsertFunctionCategory | null
  signatures?: FormulaFunctionSignature[]
}): FormulaFunctionSignature[] {
  const query = String(args.searchText ?? '').trim().toLowerCase()
  const category = args.category ?? 'All'
  const signatures = args.signatures ?? FORMULA_FUNCTION_SIGNATURES
  return signatures.filter((signature) => {
    const matchesCategory = category === 'All' || getFormulaFunctionCategory(signature) === category
    const matchesQuery = !query ||
      signature.name.toLowerCase().includes(query) ||
      signature.description.toLowerCase().includes(query)
    return matchesCategory && matchesQuery
  })
}

export function resolveInsertFunctionCatalogName(
  initialFunctionName?: string | null,
  signatures: FormulaFunctionSignature[] = FORMULA_FUNCTION_SIGNATURES,
): string | null {
  const requested = String(initialFunctionName ?? '').trim().toUpperCase()
  if (!requested) return null
  return signatures.find((signature) => signature.name.toUpperCase() === requested)?.name ?? null
}

export function selectInsertFunctionSignature(args: {
  selectedName?: string | null
  filtered: FormulaFunctionSignature[]
  signatures?: FormulaFunctionSignature[]
}): FormulaFunctionSignature {
  const signatures = args.signatures ?? FORMULA_FUNCTION_SIGNATURES
  return signatures.find((signature) => signature.name === args.selectedName) ??
    args.filtered[0] ??
    signatures[0]
}

export function buildInsertFunctionInitialKey(args: {
  initialCatalogName?: string | null
  initialArgs?: string[] | null
}): string {
  const name = args.initialCatalogName ?? ''
  return name ? `${name}:${(args.initialArgs ?? []).join('\u0001')}` : ''
}

export function buildInsertFunctionArgumentDrafts(args: {
  slots: string[]
  currentDrafts?: string[] | null
  initialArgs?: string[] | null
}): string[] {
  const currentDrafts = args.currentDrafts ?? []
  const initialArgs = args.initialArgs ?? null
  return args.slots.map((_, index) => (
    initialArgs ? initialArgs[index] ?? '' : currentDrafts[index] ?? ''
  ))
}

export function normalizeInsertFunctionArgsForCommit(args: string[]): string[] {
  return args.map((arg) => String(arg ?? '').trim()).filter(Boolean)
}

export function describeInsertFunctionCategory(signature: FormulaFunctionSignature): FormulaFunctionCategory {
  return getFormulaFunctionCategory(signature)
}
